import React from 'react';
import { useSelector } from 'react-redux';
import { Form, FormikBag, FormikProps, useFormikContext, withFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import deepEqual from 'react-fast-compare';

import { SearchEntryType } from "api";
import { ClientState } from "state/state";
import { dispatch } from "state/store-sagas";
import { isConnectedToHome } from "state/home/selectors";
import { getSetting } from "state/settings/selectors";
import { searchCloseFilterDialog, searchLoad } from "state/search/actions";
import { emptySearchFilter } from "state/search/empty";
import { SearchFilter, SearchFilterBeforeDate, SearchFilterDatePeriod, SearchTab } from "state/search/state";
import { useDispatcher } from "ui/hook";
import {
    getSafeSearchDefault,
    getSearchFilter,
    getSearchMode,
    getSearchQuery,
    getSearchTab
} from "state/search/selectors";
import { Button, ModalDialog } from "ui/control";
import { CheckboxField, SelectField, SelectFieldChoice, SelectFieldChoiceBase } from "ui/control/field";
import { BooleanOrNullString, BooleanString, toBoolean, toBooleanOrNull, toBooleanOrNullString, toBooleanString } from "util/bool-string";
import "./SearchFilterDialog.css";

type FilterField =
    "entryType" | "ownedByMe" | "repliedToMe" | "minImageCount" | "videoPresent"
    | "attachmentPresent" | "safeSearch" | "period";

const ENABLED_FIELDS: Record<SearchTab, FilterField[]> = {
    "people": ["safeSearch"],
    "content": ["ownedByMe", "repliedToMe", "minImageCount", "videoPresent", "attachmentPresent", "safeSearch", "period"],
    "postings": ["ownedByMe", "minImageCount", "videoPresent", "attachmentPresent", "safeSearch", "period"],
    "comments": ["ownedByMe", "repliedToMe", "minImageCount", "videoPresent", "attachmentPresent", "safeSearch", "period"],
    "current-blog": [
        "entryType", "ownedByMe", "repliedToMe", "minImageCount", "videoPresent", "attachmentPresent",
        "safeSearch", "period"
    ],
    "own-blog": [
        "entryType", "ownedByMe", "repliedToMe", "minImageCount", "videoPresent", "attachmentPresent",
        "safeSearch", "period"
    ]
}

const ENABLED_FIELDS_NO_USER: Record<SearchTab, FilterField[]> = {
    "people": ["safeSearch"],
    "content": ["minImageCount", "videoPresent", "attachmentPresent", "safeSearch", "period"],
    "postings": ["minImageCount", "videoPresent", "attachmentPresent", "safeSearch", "period"],
    "comments": ["minImageCount", "videoPresent", "attachmentPresent", "safeSearch", "period"],
    "current-blog": ["entryType", "minImageCount", "videoPresent", "attachmentPresent", "safeSearch", "period"],
    "own-blog": []
}

const ENTRY_TYPES: SelectFieldChoiceBase<SearchEntryType>[] = [
    {title: "any", value: "all"},
    {title: "postings", value: "posting"},
    {title: "comments", value: "comment"}
];

const AUTHOR: SelectFieldChoiceBase<BooleanString>[] = [
    {title: "anybody", value: "false"},
    {title: "i", value: "true"}
];

const IMAGE_NUMBER: SelectFieldChoice[] = [
    {title: "not-matter", value: "0"},
    {title: "present", value: "1"},
    {title: "several", value: "2"},
    {title: "many", value: "6"}
];

const VIDEO: SelectFieldChoiceBase<BooleanOrNullString>[] = [
    {title: "not-matter", value: "null"},
    {title: "present", value: "true"}
];

const ATTACHMENTS: SelectFieldChoiceBase<BooleanOrNullString>[] = [
    {title: "not-matter", value: "null"},
    {title: "present", value: "true"}
];

const BEFORE_DATE: SelectFieldChoiceBase<SearchFilterBeforeDate>[] = [
    {title: "not-matter", value: "now"},
    {title: "yesterday", value: "yesterday"},
    {title: "week-ago", value: "week"},
    {title: "month-ago", value: "month"},
    {title: "three-months-ago", value: "3-months"},
    {title: "year-ago", value: "year"}
];

const DATE_PERIOD: SelectFieldChoiceBase<SearchFilterDatePeriod>[] = [
    {title: "not-matter", value: "any"},
    {title: "today", value: "today"},
    {title: "yesterday", value: "yesterday"},
    {title: "this-week", value: "week"},
    {title: "this-month", value: "month"},
    {title: "last-three-months", value: "3-months"},
    {title: "this-year", value: "year"},
    {title: "more-year", value: "year+"}
];

interface OuterProps {
    tab: SearchTab;
    query: string;
    filter: SearchFilter;
    safeSearchDefault: boolean;
}

interface Values {
    entryType: SearchEntryType;
    ownedByMe: BooleanString;
    repliedToMe: boolean;
    minImageCount: string;
    videoPresent: BooleanOrNullString;
    attachmentPresent: BooleanOrNullString;
    safeSearch: boolean;
    beforeDate: SearchFilterBeforeDate;
    datePeriod: SearchFilterDatePeriod;
}

type Props = OuterProps & FormikProps<Values>;

function SearchFilterDialogInner({tab, safeSearchDefault}: Props) {
    const connectedToHome = useSelector(isConnectedToHome);
    const mode = useSelector(getSearchMode);
    const sheriffName = useSelector((state: ClientState) => getSetting(state, "search.sheriff-name") as string);
    const {values, setValues} = useFormikContext<Values>();
    const dispatch = useDispatcher();
    const {t} = useTranslation();

    const onClose = () => dispatch(searchCloseFilterDialog());

    const resetDisabled = deepEqual(valuesTofilter(values, safeSearchDefault), emptySearchFilter);

    const onReset = () => setValues(filterToValues(emptySearchFilter, safeSearchDefault));

    const includesComments =
        tab === "content"
        || tab === "comments"
        || ((tab === "own-blog" || tab === "current-blog") && values.entryType !== "posting");

    const fields = connectedToHome ? ENABLED_FIELDS : ENABLED_FIELDS_NO_USER;

    return (
        <ModalDialog className="search-filter-dialog" title={t("filters")} onClose={onClose}>
            <Form>
                <div className="modal-body">
                    {fields[tab].map(fieldName =>
                        <React.Fragment key={fieldName}>
                            {fieldName === "entryType" &&
                                <SelectField
                                    name="entryType"
                                    title={t("type-content")}
                                    choices={ENTRY_TYPES}
                                    labelClassName="col-4 col-lg-3"
                                    horizontal
                                />
                            }
                            {fieldName === "ownedByMe" &&
                                <SelectField
                                    name="ownedByMe"
                                    title={t("author")}
                                    choices={AUTHOR}
                                    labelClassName="col-4 col-lg-3"
                                    horizontal
                                />
                            }
                            {fieldName === "repliedToMe" && includesComments &&
                                <CheckboxField
                                    name="repliedToMe"
                                    title={t("reply-my-comment")}
                                />
                            }
                            {fieldName === "minImageCount" &&
                                <SelectField
                                    name="minImageCount"
                                    title={t("images")}
                                    choices={IMAGE_NUMBER}
                                    labelClassName="col-4 col-lg-3"
                                    horizontal
                                />
                            }
                            {fieldName === "videoPresent" &&
                                <SelectField
                                    name="videoPresent"
                                    title={t("video")}
                                    choices={VIDEO}
                                    labelClassName="col-4 col-lg-3"
                                    horizontal
                                />
                            }
                            {fieldName === "attachmentPresent" &&
                                <SelectField
                                    name="attachmentPresent"
                                    title={t("files")}
                                    choices={ATTACHMENTS}
                                    labelClassName="col-4 col-lg-3"
                                    horizontal
                                />
                            }
                            {fieldName === "safeSearch" && sheriffName &&
                                <CheckboxField
                                    name="safeSearch"
                                    title={t("safe-search")}
                                    setting="search.safe-search.default"
                                />
                            }
                            {fieldName === "period" && mode === "hashtag" &&
                                <SelectField
                                    name="beforeDate"
                                    title={t("date")}
                                    choices={BEFORE_DATE}
                                    labelClassName="col-4 col-lg-3"
                                    horizontal
                                />
                            }
                            {fieldName === "period" && mode === "fulltext" &&
                                <SelectField
                                    name="datePeriod"
                                    title={t("date")}
                                    choices={DATE_PERIOD}
                                    labelClassName="col-4 col-lg-3"
                                    horizontal
                                />
                            }
                        </React.Fragment>
                    )}
                </div>
                <div className="modal-footer">
                    <Button variant="outline-secondary" className="me-auto" disabled={resetDisabled} onClick={onReset}>
                        {t("clear-filters")}
                    </Button>
                    <Button variant="secondary" onClick={onClose}>{t("cancel")}</Button>
                    <Button variant="primary" type="submit">{t("ok")}</Button>
                </div>
            </Form>
        </ModalDialog>
    );
}

const filterToValues = (filter: SearchFilter, safeSearchDefault: boolean): Values => ({
    entryType: filter.entryType,
    ownedByMe: toBooleanString(filter.ownedByMe),
    repliedToMe: filter.repliedToMe,
    minImageCount: (filter.minImageCount ?? 0).toString(),
    videoPresent: toBooleanOrNullString(filter.videoPresent),
    attachmentPresent: toBooleanOrNullString(filter.attachmentPresent),
    safeSearch: filter.safeSearch ?? safeSearchDefault,
    beforeDate: filter.beforeDate,
    datePeriod: filter.datePeriod
});

const valuesTofilter = (values: Values, safeSearchDefault: boolean): SearchFilter => ({
    entryType: values.entryType,
    ownedByMe: toBoolean(values.ownedByMe),
    repliedToMe: values.repliedToMe,
    minImageCount: values.minImageCount === "0" ? null : parseInt(values.minImageCount),
    videoPresent: toBooleanOrNull(values.videoPresent),
    attachmentPresent: toBooleanOrNull(values.attachmentPresent),
    safeSearch: values.safeSearch === safeSearchDefault ? null : values.safeSearch,
    beforeDate: values.beforeDate,
    datePeriod: values.datePeriod
});

const searchFilterDialogLogic = {

    mapPropsToValues: (props: OuterProps): Values => filterToValues(props.filter, props.safeSearchDefault),

    handleSubmit(values: Values, formik: FormikBag<OuterProps, Values>): void {
        const filter = valuesTofilter(values, formik.props.safeSearchDefault);
        dispatch(searchLoad(formik.props.query, formik.props.tab, filter));
        dispatch(searchCloseFilterDialog());
        formik.setSubmitting(false);
    }

};

const SearchFilterDialogOuter = withFormik(searchFilterDialogLogic)(SearchFilterDialogInner);

export default function SearchFilterDialog() {
    const tab = useSelector(getSearchTab);
    const query = useSelector(getSearchQuery);
    const filter = useSelector(getSearchFilter);
    const safeSearchDefault = useSelector(getSafeSearchDefault);

    return <SearchFilterDialogOuter tab={tab} query={query} filter={filter} safeSearchDefault={safeSearchDefault}/>;
}
